import React, { useState, useEffect } from "react";
import {
  createEmployee,
  checkEmployeeIdExists,
  getOfficeByTenant,
  getOffices,
  getShiftsByOffice,
} from "../../../services/hrms.api";
import { useNavigate } from "react-router-dom";
import { Upload, X } from "lucide-react";

export default function EmployeeForm() {
  const nav = useNavigate();

  const [tab, setTab] = useState("basic");
  const [saving, setSaving] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const [empIdStatus, setEmpIdStatus] = useState("idle");

  const [officeLoading, setOfficeLoading] = useState(false);
  const [officeError, setOfficeError] = useState("");

  // extra local state for "Other" options
  const [customDepartment, setCustomDepartment] = useState("");
  const [customDesignation, setCustomDesignation] = useState("");

  const [form, setForm] = useState({
    // Basic
    firstName: "",
    lastName: "",
    employeeId: "",
    joinDate: "",
    dob: "",
    gender: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    company: "",
    department: "",
    designation: "",
    address: "",
    monthlySalary: "",
    panNumber: "",
    aadhaarNumber: "",
    status: "active",

    // Permissions (mock)
    permissions: {
      employees: { view: true, create: false, edit: false, remove: false },
      attendance: { view: true, punch: false },
      payroll: { view: false, export: false },
      gps: { view: false },
      holidays: {
        read: true,
        write: false,
        create: false,
        delete: false,
        import: false,
        export: false,
      },
      leaves: {
        read: true,
        write: false,
        create: false,
        delete: false,
        import: false,
        export: false,
      },
    },
  });

  const [offices, setOffices] = useState([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState("");
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState("");

  const [shiftLoading, setShiftLoading] = useState(false);
  const [shiftError, setShiftError] = useState("");


  // ✅ TenantId from stored user object (matches what you showed)
  const tenantId = (() => {
    try {
      const raw = localStorage.getItem("user");
      const u = raw ? JSON.parse(raw) : null;
      return u?.tenant || "";
    } catch {
      return "";
    }
  })();

  // ✅ Debounced EmployeeId
  const debouncedEmployeeId = useDebouncedValue(form.employeeId, 500);

  // --- helpers ---
  const onChange = (e) =>
    setForm((p) => ({
      ...p,
      [e.target.name]: e.target.value,
    }));

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      alert("Image must be below 4 MB.");
      return;
    }
    setImgUrl(URL.createObjectURL(file));
  };

  const clearImage = () => {
    if (imgUrl) URL.revokeObjectURL(imgUrl);
    setImgUrl("");
  };

  const validate = () => {
    if (!form.firstName || !form.lastName)
      return "First name and Last name are required.";
    if (!form.employeeId) return "Employee ID is required.";
    if (!form.joinDate) return "Joining Date is required.";
  // ✅ NEW
    if (!form.username) return "Username is required.";
    if (!form.password) return "Password is required.";
    if (form.password.length < 6)
      return "Password must be at least 6 characters.";

    if (form.password !== form.confirmPassword)
      return "Passwords do not match.";
    if (!form.phone) return "Phone number is required.";
    if (!form.company) return "Company is required.";

    // PAN format (optional)
    if (form.panNumber) {
      const pan = form.panNumber.trim().toUpperCase();
      const panOk = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
      if (!panOk) return "PAN number format is invalid (example: ABCDE1234F).";
    }

    // Aadhaar format (optional)
    if (form.aadhaarNumber) {
      const a = form.aadhaarNumber.replace(/\s+/g, "");
      const aadhaarOk = /^[0-9]{12}$/.test(a);
      if (!aadhaarOk) return "Aadhaar must be 12 digits.";
    }

    if (form.monthlySalary) {
      const n = Number(form.monthlySalary);
      if (Number.isNaN(n) || n < 0)
        return "Monthly salary must be a valid number.";
    }

    if (!selectedOfficeId) return "Office is required.";
    if (!selectedShiftId) return "Shift is required.";


    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // ✅ do this first (faster feedback)
    if (empIdStatus === "exists") {
      alert("Employee ID already exists.");
      return;
    }
    if (empIdStatus === "checking") {
      alert("Please wait until Employee ID check completes.");
      return;
    }

    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    setSaving(true);
    try {
      const t = (s) => (s ?? "").trim();

      const finalDepartment =
        form.department === "OTHER" ? customDepartment : form.department;
      const finalDesignation =
        form.designation === "OTHER" ? customDesignation : form.designation;

      const payload = {
        employeeId: t(form.employeeId),
        firstName: t(form.firstName),
        lastName: t(form.lastName),
        company: t(form.company),
        joinDate: form.joinDate,
        dob: form.dob,
        gender: t(form.gender),
        address: t(form.address),
        department: t(finalDepartment),
        designation: t(finalDesignation),
        permissions: form.permissions,
        username: t(form.username).toLowerCase(), // ✅ NEW
        password: form.password,                  // ✅ NEW
      };
      // OPTIONALS: include only if filled
      if (t(form.email)) payload.email = t(form.email);
      if (t(form.phone)) payload.phone = t(form.phone);

      if (t(form.panNumber))
        payload.panNumber = t(form.panNumber).toUpperCase();
      if (t(form.aadhaarNumber))
        payload.aadhaarNumber = t(form.aadhaarNumber).replace(/\s+/g, "");

      if (String(form.monthlySalary || "").trim())
        payload.monthlySalary = Number(form.monthlySalary);
      payload.officeId = selectedOfficeId;
      payload.shiftId = selectedShiftId;

      await createEmployee(payload);
      nav("/admin/hrms/employees", { replace: true });
    } catch (e1) {
      alert(e1?.message || "Failed to save employee.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ Fetch Office by tenantId -> set company (locked)
  useEffect(() => {
    if (!tenantId) {
      setOfficeError("Tenant not found. Please login again.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setOfficeError("");
        setOfficeLoading(true);

        const { data } = await getOfficeByTenant(tenantId);

        // backend returns: { items: [...] }
        const office = Array.isArray(data?.items) ? data.items[0] : null;
        const name = office?.name || "";

        if (!cancelled) {
          setForm((p) => ({
            ...p,
            company: name || p.company,
          }));
        }
      } catch (e) {
        if (!cancelled) {
          setOfficeError(e?.message || "Failed to load office.");
        }
      } finally {
        if (!cancelled) setOfficeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    const id = (debouncedEmployeeId || "").trim();
    if (!id) {
      setEmpIdStatus("idle");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setEmpIdStatus("checking");
        const res = await checkEmployeeIdExists(id); // expects { exists: boolean }
        if (!cancelled) setEmpIdStatus(res?.exists ? "exists" : "available");
      } catch {
        if (!cancelled) setEmpIdStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedEmployeeId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await getOffices(); // GET /api/offices
        const items = res?.data?.items || [];
        if (!cancelled) setOffices(items);
      } catch {
        if (!cancelled) setOfficeError("Failed to load offices");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedOfficeId) {
      setShifts([]);
      setSelectedShiftId("");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setShiftLoading(true);
        setShiftError("");

        const res = await getShiftsByOffice(selectedOfficeId);
        const items = res?.data?.data || res?.data || [];

        if (!cancelled) setShifts(items);
      } catch {
        if (!cancelled) setShiftError("Failed to load shifts");
      } finally {
        if (!cancelled) setShiftLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedOfficeId]);


  return (
    <div className="p-6">
      <div className="mb-4 flex items-center gap-2 dark:text-white">
        {form.employeeId ? (
          <span className="text-sm text-gray-500">
            Employee ID : {form.employeeId}
          </span>
        ) : null}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-white/10">
        <nav className="-mb-px flex gap-6">
          <button
            className={`border-b-2 px-3 pb-3 text-sm ${tab === "basic"
              ? "border-white font-semibold text-white"
              : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            onClick={() => setTab("basic")}
            type="button"
          >
            Basic Information
          </button>
        </nav>
      </div>

      <form onSubmit={onSubmit} className="mt-4 space-y-6">
        <>
          {/* Upload */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
            <div className="flex items-center gap-4">
              <div className="dark:bg-transparent h-16 w-16 overflow-hidden rounded-full bg-white ring-1 ring-gray-200 dark:ring-white/10">
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt="avatar"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                    No Image
                  </div>
                )}
              </div>

              <div className="text-sm text-gray-500">
                <div className="mb-2 font-medium text-gray-700 dark:text-white">
                  Upload Profile Image
                </div>
                <div>Image should be below 4 mb</div>
              </div>

              <div className="ml-auto flex items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-3 py-2 text-sm text-white">
                  <Upload className="h-4 w-4" /> Upload
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImage}
                  />
                </label>

                {imgUrl && (
                  <button
                    type="button"
                    onClick={clearImage}
                    className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 dark:text-white dark:hover:bg-white/10"
                  >
                    <X className="h-4 w-4" /> Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Two column grid */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Text
              label="First Name *"
              name="firstName"
              value={form.firstName}
              onChange={onChange}
            />
            <Text
              label="Last Name *"
              name="lastName"
              value={form.lastName}
              onChange={onChange}
            />

            {/* Employee ID + status */}
            <div>
              <Text
                label="Employee ID *"
                name="employeeId"
                value={form.employeeId}
                onChange={onChange}
              />
              

              {empIdStatus === "checking" && (
                <div className="mt-1 text-xs text-gray-400">
                  Checking Employee ID…
                </div>
              )}
              {empIdStatus === "exists" && (
                <div className="mt-1 text-xs font-medium text-red-500">
                  Employee ID already exists
                </div>
              )}
              {empIdStatus === "available" && (
                <div className="mt-1 text-xs font-medium text-green-500">
                  Employee ID is available
                </div>
              )}
              {empIdStatus === "error" && (
                <div className="mt-1 text-xs text-orange-400">
                  Unable to verify Employee ID
                </div>
              )}
            </div>

                {/* ✅ Username / Password — now proper siblings, own grid cells */}
            <Text
              label="Username *"
              name="username"
              value={form.username}
              onChange={onChange}
              autoComplete="off"
            />
            <Text
              label="Password *"
              name="password"
              type="password"
              value={form.password}
              onChange={onChange}
              autoComplete="new-password"
            />
            <Text
              label="Confirm Password *"
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={onChange}
              autoComplete="new-password"
            />

            <div>
              <Text
                label="Company *"
                name="company"
                value={officeLoading ? "Loading…" : form.company}
                disabled
              />
              {officeError ? (
                <div className="mt-1 text-xs text-red-500">{officeError}</div>
              ) : null}
            </div>

            <div>
              <Label text="Office *" />
              <select
                value={selectedOfficeId}
                onChange={(e) => {
                  setSelectedOfficeId(e.target.value);
                  setSelectedShiftId("");
                }}
                className="dark:bg-transparent mt-1 w-full rounded-lg border px-3 py-2 text-sm
      dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="">Select Office</option>
                {offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label text="Shift *" />
              <select
                value={selectedShiftId}
                disabled={!selectedOfficeId || shiftLoading}
                onChange={(e) => setSelectedShiftId(e.target.value)}
                className="dark:bg-transparent mt-1 w-full rounded-lg border px-3 py-2 text-sm
      disabled:opacity-60 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="">
                  {shiftLoading ? "Loading shifts..." : "Select Shift"}
                </option>
                {shifts.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({s.startTime} - {s.endTime})
                  </option>
                ))}
              </select>

              {shiftError && (
                <div className="mt-1 text-xs text-red-500">{shiftError}</div>
              )}
            </div>


            <Text
              label="Joining Date *"
              name="joinDate"
              type="date"
              value={form.joinDate}
              onChange={onChange}
            />
            <Text
              label="Date of Birth"
              name="dob"
              type="date"
              value={form.dob}
              onChange={onChange}
            />

            <Select
              label="Gender"
              name="gender"
              value={form.gender}
              onChange={onChange}
              options={["Male", "Female", "Other"]}
            />
            <Text
              label="Phone Number *"
              name="phone"
              value={form.phone}
              onChange={onChange}
            />

            <Text
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
            />
            <Text
              label="PAN Number"
              name="panNumber"
              value={form.panNumber}
              onChange={onChange}
              placeholder="ABCDE1234F"
            />
            <Text
              label="Aadhaar Number"
              name="aadhaarNumber"
              value={form.aadhaarNumber}
              onChange={onChange}
              placeholder="12-digit Aadhaar"
            />

            <Text
              label="Monthly Salary (30 days)"
              name="monthlySalary"
              type="number"
              value={form.monthlySalary}
              onChange={onChange}
            />

            <Select
              label="Department"
              name="department"
              value={form.department}
              onChange={onChange}
              options={[
                "Finance",
                "Human Resources",
                "Sales",
                "IT",
                "Operations",
                "Other",
              ]}
            />
            {form.department === "OTHER" && (
              <Text
                label="Other Department"
                name="customDepartment"
                value={customDepartment}
                onChange={(e) => setCustomDepartment(e.target.value)}
              />
            )}

            <Select
              label="Designation"
              name="designation"
              value={form.designation}
              onChange={onChange}
              options={[
                "Accountant",
                "Software Developer",
                "Manager",
                "HR Executive",
                "Executive Officer",
                "Other",
              ]}
            />
            {form.designation === "OTHER" && (
              <Text
                label="Other Designation"
                name="customDesignation"
                value={customDesignation}
                onChange={(e) => setCustomDesignation(e.target.value)}
              />
            )}
          </div>

          {/* Address */}
          <div>
            <Label text="Address" />
            <textarea
              name="address"
              value={form.address}
              onChange={onChange}
              rows={3}
              className="dark:bg-transparent mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
              placeholder="Employee address"
            />
          </div>
        </>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 dark:text-white dark:hover:bg-white/10"
            onClick={() => nav(-1)}
          >
            Cancel
          </button>

          <button
            disabled={
              saving || empIdStatus === "exists" || empIdStatus === "checking"
            }
            className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- small UI atoms ---------- */
function Label({ text }) {
  return (
    <label className="text-sm font-medium text-gray-700 dark:text-white">
      {text}
    </label>
  );
}

function Text({ label, ...rest }) {
  return (
    <div>
      <Label text={label} />
      <input
        {...rest}

        className="dark:bg-transparent mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
      />
    </div>
  );
}

function Select({ label, name, value, onChange, options = [] }) {
  return (
    <div>
      <Label text={label} />
      <select
        name={name}
        value={value}
        onChange={(e) => {
          const val = e.target.value === "Other" ? "OTHER" : e.target.value;
          onChange({ target: { name, value: val } });
        }}
        className="dark:bg-transparent mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
      >
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ---------- debounce hook ---------- */
function useDebouncedValue(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}